import React, { useEffect } from 'react';
import { SypacInput, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import Tons from '../../../../assets/Tons';
import PriceTag from '../../../../assets/PriceTag';
import { SectionProps } from '../addNewContract.interface';
import { numberTwoDecimal } from '../../../../utils/number.util';
import { NumericFormat } from 'react-number-format';
import { cn } from '../../../../lib/utils';
import TooltipCircle from '../../../../assets/TooltipCircle';
import { Tooltip } from 'react-tooltip';

const PriceQuantitySection: React.FC<SectionProps> = ({ formik }) => {
	const { t } = useTranslate();

	const toNum = (v: any) => {
		const n = Number(String(v ?? '').replace(',', '.'));
		return isNaN(n) ? 0 : n;
	};

	const formatNumber = (value: number) => {
		if (!Number.isFinite(value)) return '';
		const rounded = Math.round(value * 100) / 100;
		return Number.isInteger(rounded)
			? String(rounded)
			: rounded.toFixed(2).replace(/\.?0+$/, '');
	};

	const isValidNumber = (val: string, allowNegative = false) => {
		const regex = allowNegative ? /^-?\d*[.,]?\d*$/ : /^\d*[.,]?\d*$/;
		if (!regex.test(val)) return false;

		const parts = val.split(/[.,]/);
		if (parts.length > 1 && parts[1].length > 10) {
			return false;
		}

		return true;
	};

	const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!isValidNumber(e.target.value)) return;
		formik.handleChange(e);
	};

	const qty = toNum(formik.values.quantity);
	const seller = toNum(formik.values.pricePerTonSeller);
	const buyer = toNum(formik.values.pricePerTonBuyer);

	const overdraftRaw = String(
		formik.values.allowedOverdraftPercent ?? '',
	).trim();
	const overdraftValid =
		overdraftRaw !== '' && Number.isFinite(toNum(overdraftRaw));
	const overdraftedQty =
		qty > 0 && overdraftValid ? qty * (1 + toNum(overdraftRaw) / 100) : null;
	const overdraftedQtyFormatted = overdraftedQty
		? formatNumber(overdraftedQty)
		: '';

	const canCalculateProfitLoss =
		qty > 0 &&
		formik.values.pricePerTonSeller !== '' &&
		formik.values.pricePerTonBuyer !== '';

	const profitDifference = (buyer - seller) * qty;
	const isLoss = profitDifference < 0;
	const formattedProfit = canCalculateProfitLoss
		? numberTwoDecimal(Math.abs(profitDifference))
		: null;

	const showProfitDash = !canCalculateProfitLoss || formattedProfit === null;

	const markupRaw = String(formik.values.markup ?? '').trim();
	const hasMarkup = markupRaw !== '' && markupRaw !== '-';
	const markup = hasMarkup ? toNum(markupRaw) : NaN;

	const calcMarkup = (s: number, b: number) => {
		if (s === 0) return b > 0 ? 100 : 0;
		return ((b - s) / s) * 100;
	};

	const calcBuyer = (s: number, m: number) => s * (1 + m / 100);
	const calcSeller = (b: number, m: number) => {
		const denom = 1 + m / 100;
		return denom !== 0 ? b / denom : 0;
	};

	useEffect(() => {
		const sStr = String(formik.values.pricePerTonSeller ?? '').trim();
		const bStr = String(formik.values.pricePerTonBuyer ?? '').trim();
		const mRaw = String(formik.values.markup ?? '').trim();
		const markupEmpty = mRaw === '' || mRaw === '-';

		if (!markupEmpty || sStr === '' || bStr === '') return;

		const id = setTimeout(() => {
			formik.setFieldValue(
				'markup',
				formatNumber(calcMarkup(toNum(sStr), toNum(bStr))),
				false,
			);
		}, 500);

		return () => clearTimeout(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik.values.pricePerTonSeller,
		formik.values.pricePerTonBuyer,
		formik.values.markup,
	]);

	const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		if (!isValidNumber(value)) return;

		formik.handleChange(e);

		const nextValue = toNum(value);
		const nextSeller = name === 'pricePerTonSeller' ? nextValue : seller;
		const nextBuyer = name === 'pricePerTonBuyer' ? nextValue : buyer;

		const currentSellerStr =
			name === 'pricePerTonSeller'
				? value
				: String(formik.values.pricePerTonSeller);
		const currentBuyerStr =
			name === 'pricePerTonBuyer'
				? value
				: String(formik.values.pricePerTonBuyer);

		if (currentSellerStr !== '' && currentBuyerStr !== '') {
			formik.setFieldValue(
				'markup',
				formatNumber(calcMarkup(nextSeller, nextBuyer)),
				false,
			);
		} else if (hasMarkup && Number.isFinite(markup)) {
			if (name === 'pricePerTonSeller' && value !== '') {
				formik.setFieldValue(
					'pricePerTonBuyer',
					formatNumber(calcBuyer(nextValue, markup)),
					false,
				);
			}
			if (name === 'pricePerTonBuyer' && value !== '') {
				formik.setFieldValue(
					'pricePerTonSeller',
					formatNumber(calcSeller(nextValue, markup)),
					false,
				);
			}
		}

		setTimeout(() => formik.setFieldTouched(name, true, true), 0);
	};

	const handleMarkupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		if (!isValidNumber(value, true)) return;

		formik.handleChange(e);

		const markupRaw = value.trim();
		const hasMarkupValue = markupRaw !== '' && markupRaw !== '-';
		const markupValue = hasMarkupValue ? toNum(markupRaw) : NaN;
		if (!hasMarkupValue || !Number.isFinite(markupValue)) return;

		if (String(formik.values.pricePerTonSeller) !== '') {
			formik.setFieldValue(
				'pricePerTonBuyer',
				formatNumber(calcBuyer(seller, markupValue)),
				false,
			);
		} else if (String(formik.values.pricePerTonBuyer) !== '') {
			formik.setFieldValue(
				'pricePerTonSeller',
				formatNumber(calcSeller(buyer, markupValue)),
				false,
			);
		}
	};

	return (
		<>
			<SypacText variant="body-regular-medium">
				<p className="text-gray-40 text-[14px] leading-[16px] mx-3">
					<T keyName="adminContracts.selectPriceQuantity">
						Select price & quantity
					</T>
				</p>
			</SypacText>

			<div className="h-fit flex gap-3 items-start bg-[#EBECF2] border border-solid border-gray-10 rounded-10 p-[10px] mx-3">
				<SypacInput
					error={!!(formik.touched.quantity && formik.errors.quantity)}
					className="relative w-full"
				>
					<SypacText className="mb-2" variant="overline-normal-large">
						<p>
							<T keyName="adminContracts.productQuantity">Product quantity</T>{' '}
							<span className="text-red">*</span>
						</p>
					</SypacText>

					<input
						className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
							formik.touched.quantity && formik.errors.quantity
								? 'border-red-orange'
								: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
						}`}
						name="quantity"
						type="text"
						inputMode="decimal"
						pattern="[0-9]*[.,]?[0-9]*"
						placeholder={t('adminContracts.enterQuantity', 'Enter quantity')}
						value={formik.values.quantity}
						onChange={handleNumericChange}
					/>

					<div className="absolute top-[36px] left-[10px]">
						<Tons color="#A2A2A2" />
					</div>
					<div className="absolute top-[24px] right-[12px]">
						<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
							t
						</p>
					</div>

					{formik.touched.quantity && formik.errors.quantity ? (
						<span className="input-error">{formik.errors.quantity}</span>
					) : null}
				</SypacInput>

				<SypacInput className="relative w-[180px]">
					<SypacText className="mb-2" variant="overline-normal-large">
						<p>
							<T keyName="adminContracts.overdraftedQuantity">
								Overdrafted quantity
							</T>
						</p>
					</SypacText>

					<input
						className="w-[180px] py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition border-gray-10 bg-white cursor-default"
						name="overdraftedQuantity"
						type="text"
						readOnly
						tabIndex={-1}
						placeholder="—"
						value={overdraftedQtyFormatted}
						style={{ caretColor: 'transparent' }}
					/>

					<div className="absolute top-[36px] left-[10px]">
						<Tons color="#A2A2A2" />
					</div>
					<div className="absolute top-[24px] right-[12px]">
						<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
							t
						</p>
					</div>
				</SypacInput>
			</div>

			<div className="flex gap-3 mx-3">
				<div className="w-full flex flex-col">
					<div className="h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
						<SypacInput
							error={
								!!(
									formik.touched.pricePerTonSeller &&
									formik.errors.pricePerTonSeller
								)
							}
							className="relative w-full"
						>
							<SypacText className="mb-2" variant="overline-normal-large">
								<p>
									<T keyName="adminContracts.pricePerTonSeller">
										Price per ton (Seller)
									</T>{' '}
									<span className="text-red">*</span>
								</p>
							</SypacText>
							<input
								className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
									formik.touched.pricePerTonSeller &&
									formik.errors.pricePerTonSeller
										? 'border-red-orange'
										: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
								}`}
								name="pricePerTonSeller"
								type="text"
								inputMode="decimal"
								pattern="[0-9]*[.,]?[0-9]*"
								placeholder={t(
									'adminContracts.enterProductPriceSeller',
									'Enter seller price',
								)}
								value={formik?.values.pricePerTonSeller}
								onChange={handlePriceChange}
							/>
							<div className="absolute top-[36px] left-[10px]">
								<PriceTag color="#A2A2A2" />
							</div>
							<div className="absolute top-[24px] right-[12px]">
								<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
									{formik.values.countryCode === 'MD' ? 'MDL' : 'PLN'} / t
								</p>
							</div>
						</SypacInput>
					</div>
					{formik?.touched.pricePerTonSeller &&
					formik?.errors.pricePerTonSeller ? (
						<span className="input-error">
							{formik.errors.pricePerTonSeller}
						</span>
					) : null}
				</div>

				<div className="w-full flex flex-col">
					<div className="h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
						<SypacInput
							error={
								!!(
									formik.touched.pricePerTonBuyer &&
									formik.errors.pricePerTonBuyer
								)
							}
							className="relative w-full"
						>
							<SypacText className="mb-2" variant="overline-normal-large">
								<p>
									<T keyName="adminContracts.pricePerTonBuyer">
										Price per ton (Buyer)
									</T>{' '}
									<span className="text-red">*</span>
								</p>
							</SypacText>
							<input
								className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
									formik.touched.pricePerTonBuyer &&
									formik.errors.pricePerTonBuyer
										? 'border-red-orange'
										: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
								}`}
								name="pricePerTonBuyer"
								type="text"
								inputMode="decimal"
								pattern="[0-9]*[.,]?[0-9]*"
								placeholder={t(
									'adminContracts.enterProductPriceBuyer',
									'Enter buyer price',
								)}
								value={formik?.values.pricePerTonBuyer}
								onChange={handlePriceChange}
							/>
							<div className="absolute top-[36px] left-[10px]">
								<PriceTag color="#A2A2A2" />
							</div>
							<div className="absolute top-[24px] right-[12px]">
								<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
									{formik.values.countryCode === 'MD' ? 'MDL' : 'PLN'} / t
								</p>
							</div>
						</SypacInput>
					</div>
					{formik?.touched.pricePerTonBuyer &&
					formik?.errors.pricePerTonBuyer ? (
						<span className="input-error">
							{formik.errors.pricePerTonBuyer}
						</span>
					) : null}
				</div>
			</div>

			<div className="flex gap-3 mx-3">
				<div className="w-full flex flex-col">
					<div className="h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
						<SypacInput
							error={!!(formik.touched.markup && formik.errors.markup)}
							className="relative w-full"
						>
							<SypacText className="mb-2" variant="overline-normal-large">
								<p>
									<T keyName="adminContracts.markUp">Mark-up</T>{' '}
									<span className="text-red">*</span>
								</p>
							</SypacText>
							<input
								className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
									formik.touched.markup && formik.errors.markup
										? 'border-red-orange'
										: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
								}`}
								name="markup"
								type="text"
								inputMode="decimal"
								pattern="-?[0-9]*[.,]?[0-9]*"
								placeholder={t('adminContracts.enterMarkUp', 'Enter mark-up')}
								value={formik?.values.markup}
								onChange={handleMarkupChange}
							/>
							<div className="absolute top-[36px] left-[10px]">
								<PriceTag color="#A2A2A2" />
							</div>
							<div className="absolute top-[24px] right-[12px]">
								<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
									%
								</p>
							</div>
						</SypacInput>
					</div>
					{formik?.touched.markup && formik?.errors.markup ? (
						<span className="input-error">{formik.errors.markup}</span>
					) : null}
				</div>

				<div className="w-full flex flex-col">
					<div className="h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
						<SypacInput
							error={
								!!(
									formik.touched.allowedOverdraftPercent &&
									formik.errors.allowedOverdraftPercent
								)
							}
							className="relative w-full"
						>
							<div className="h-[16px] flex items-center gap-2 mb-2">
								<SypacText variant="overline-normal-large">
									<p>
										<T keyName="adminContracts.overdraft">Overdraft</T>
									</p>
								</SypacText>
								<span
									data-tooltip-id="price-quantity-tooltip"
									data-tooltip-content={t(
										'generalTab.overdraftTooltip',
										'Overdraft: A percentage',
									)}
									className="p-0 m-0 mb-[2px] flex scale-[1.1]"
								>
									<TooltipCircle />
								</span>
							</div>
							<input
								className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
									formik.touched.allowedOverdraftPercent &&
									formik.errors.allowedOverdraftPercent
										? 'border-red-orange'
										: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
								}`}
								name="allowedOverdraftPercent"
								type="text"
								inputMode="decimal"
								pattern="[0-9]*[.,]?[0-9]*"
								placeholder={t(
									'adminContracts.enterOverdraft',
									'Enter overdraft',
								)}
								value={formik.values.allowedOverdraftPercent}
								onChange={handleNumericChange}
							/>
							<div className="absolute top-[36px] left-[10px]">
								<PriceTag color="#A2A2A2" />
							</div>
							<div className="absolute top-[24px] right-[12px]">
								<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
									%
								</p>
							</div>
						</SypacInput>
					</div>
					{formik.touched.allowedOverdraftPercent &&
					formik.errors.allowedOverdraftPercent ? (
						<span className="input-error">
							{formik.errors.allowedOverdraftPercent}
						</span>
					) : null}
				</div>

				<div
					className={cn(
						'w-[calc(100%-144.9px)] h-fit flex flex-col gap-[10px] items-center justify-center border border-solid rounded-10 px-[6px] py-[19px]',
						canCalculateProfitLoss && isLoss
							? 'bg-[#F1CACE]/20 border-[#F2A6AA]'
							: 'bg-alabaster border-gray-10/50',
					)}
				>
					<SypacText variant="overline-normal-large">
						<p>
							<T
								keyName={
									isLoss ? 'adminContracts.loss' : 'adminContracts.profit'
								}
							>
								{isLoss ? 'Loss' : 'Profit'}
							</T>
						</p>
					</SypacText>

					<SypacText variant="overline-normal-large">
						<p className="text-[16px] leading-[24px]">
							{showProfitDash ? (
								'—'
							) : (
								<NumericFormat
									type="text"
									displayType="text"
									thousandSeparator=" "
									decimalSeparator="."
									decimalScale={2}
									value={formattedProfit}
									suffix={formik.values.countryCode === 'MD' ? ' MDL' : ' PLN'}
									prefix={isLoss ? '-' : ''}
									allowNegative={false}
								/>
							)}
						</p>
					</SypacText>
				</div>
			</div>
			<Tooltip
				place="top"
				id="price-quantity-tooltip"
				style={{
					backgroundColor: '#E8E8E8',
					color: '#000000',
					whiteSpace: 'normal',
					maxWidth: '248px',
				}}
			/>
		</>
	);
};

export default PriceQuantitySection;
